import { finalize } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpProgressEvent, HttpRequest } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { FileLikeObject, FileUploader } from 'ng2-file-upload';
import { BannerNotificationsService } from '../../xform-compat';
import { CONFIG } from '../../config';
import { Attachment } from '../../shared/models';


@Injectable({
  providedIn: 'root'
})

export class FileUploadService {
  private uploadingError: boolean;
  private keyPaperUploadedSource: Subject<void> = new Subject<void>();
  public keyPaperUploaded$ = this.keyPaperUploadedSource.asObservable();
  public uploader: FileUploader;
  public onFileAdded: Observable<boolean>;
  public fileIndex: number;
  public fileUploadProgress = 0;
  public uploadInProgress = false;
  public uploadMessage: string;
  public fileUploadResponse$: Subject<Array<Attachment>> = new Subject<Array<Attachment>>();
  public fileUploadMapOnePagerResponse$: Subject<any> = new Subject<any>();

  constructor(
    private bannerNotificationsService: BannerNotificationsService,
    private translateService: TranslateService,
    private http: HttpClient
  ) {
    this.uploaderSettings();
  }

  private uploaderSettings() {
    this.uploader = new FileUploader({
      url: '/',
      autoUpload: false,
      disableMultipart: false,
      allowedMimeType: CONFIG.uploadSettings.allowedTypes,
      maxFileSize: CONFIG.uploadSettings.maxFileSize
    });

    this.uploader.onAfterAddingFile = () => {
      this.uploadingError = false;
      this.onFileAdded = new Observable(observer => {
        this.cleanQueue();
        observer.next(true);
      });
    };

    this.uploader.onWhenAddingFileFailed = (item: FileLikeObject, filter: any) => {
      let errorMsg: string;
      let params: object;
      switch (filter.name) {
        case 'fileSize':
          errorMsg = 'ERRORS.FILE_SIZE';
          params = {
            file_size: (Math.round(item.size) / 1000),
            max_size: (Math.round(CONFIG.uploadSettings.maxFileSize) / 1000)
          };
          break;
        case 'mimeType':
          errorMsg = 'ERRORS.MIMETYPE';
          params = {
            type: item.type
          };
          break;
        default:
          errorMsg = 'ERRORS.UNKNOWN';
          params = {
            filter: filter.name
          };
      }
      this.uploadingError = true;
      this.bannerNotificationsService.error(this.translateService.instant(errorMsg, params));
    };
  }

  public convertToBase64(evt) {
    return new Promise<string>((resolve) => {
      const files = evt.target.files;
      const file = files[0];
      let binaryString: string;
      let transformedImage: string;
      if (files && file && this.uploader.queue.length > 0 && !this.uploadingError) {
        const reader = new FileReader();
        reader.onload = (readerEvt: any) => {
          binaryString = readerEvt.target.result;
          transformedImage = btoa(binaryString);
          resolve(transformedImage);
        };
        reader.readAsBinaryString(file);
        this.fileIndex = this.uploader.queue.length - 1;
      }
    });
  }

  public cleanQueue() {
    if (this.uploader.queue.length > 0) {
      this.uploader.clearQueue();
    }
  }

  /**
   * Process file upload
   * fileUploadProgress can be used to display the progress bar
   * uploadInProgress can be used to enable/disable elements during the process
   * uploadMessage can be used to display an status message
   * @param filesToUpload
   * @param attachmentList
   * @param isEdit
   * @param inputId: DOM ID element to clear on complete the process, fails or completes
   * @param isPublic
   */
  public uploadFiles(
    filesToUpload: FileList,
    inputId?: string,
    attachmentList?: Array<Attachment>,
    isEdit?: boolean,
    isPublic?: boolean
  ): void {
    const url = !isPublic ? '/api/rms/attachments/bulk' : '/api/rms/attachments/bulk/public';
    const formData: FormData = this.processFiles(filesToUpload);
    this.http.request(new HttpRequest('POST', url, formData, { reportProgress: true })).pipe(
      finalize(() => this.resetOnFileProcess(inputId)))
      .subscribe(event => {
          if (event.type === HttpEventType.UploadProgress) {
            this.runFileUploadProgress(event);
          } else if (event.type === HttpEventType.Response) {
            this.displayUploadedMessage('File(s) Uploaded!', 'text-success');
            if (!isEdit) {
              attachmentList.push(...event.body as Array<Attachment>);
            } else {
              this.fileUploadResponse$.next(event.body as Array<Attachment>);
            }
          }
        },
        error1 => this.errorProcessingUpload(error1)
      );
  }

  /**
   * Process file upload
   * fileUploadProgress can be used to display the progress bar
   * uploadInProgress can be used to enable/disable elements during the process
   * uploadMessage can be used to display an status message
   * @param filesToUpload
   * @param indicationId
   * @param attachmentList
   * @param isEdit
   * @param inputId: DOM ID element to clear on complete the process, fails or completes
   * @param url: endpoint to submit the file
   */
  public uploadFilesMapOnePager(
    filesToUpload: FileList,
    indicationId: number,
    inputId?: string,
    attachmentList?: Array<Attachment>,
    isEdit?: boolean,
    url = `/api/rms/indications/${indicationId}/maponepager`
  ): Promise<void> {
    return new Promise<void>(resolve => {
      const formData: FormData = this.processFiles(filesToUpload);
      this.http.request(new HttpRequest('POST', url, formData, { reportProgress: true }))
        .pipe(
          finalize(() => {
            this.resetOnFileProcess(inputId);
            resolve();
          })
        )
        .subscribe(event => {
            if (event.type === HttpEventType.UploadProgress) {
              this.runFileUploadProgress(event);
            } else if (event.type === HttpEventType.Response) {
              this.displayUploadedMessage('File(s) Uploaded!', 'text-success');
              if (!isEdit) {
                attachmentList.push(...event.body as Array<Attachment>);
              }
              this.fileUploadMapOnePagerResponse$.next(event.body);
            }
          },
          error1 => this.errorProcessingUpload(error1)
        );
    });
  }

  /**
   * Shows then hides the status message
   * @param message: what to display
   * @param cssClass: css class, usually for coloring
   */
  private displayUploadedMessage(message: string, cssClass = 'text-info'): void {
    this.uploadMessage = `<span class="${cssClass}"><em>${message}</em></span>`;
    setTimeout(() => this.uploadMessage = '', 5000);
  }

  public keyPapersUpload(files: FileList, endpoint: string, entityId: number, inputId: string): Promise<void> {
    return new Promise<void>(resolve => {
      endpoint = endpoint.replace('{entityId}', entityId.toString());
      const formData: FormData = this.processFiles(files);
      this.http.request(new HttpRequest('POST', `/api/rms/${endpoint}`, formData, { reportProgress: true })).pipe(
        finalize(() => {
          this.resetOnFileProcess(inputId);
          resolve();
        }))
        .subscribe(
          event => {
            if (event.type === HttpEventType.UploadProgress) {
              this.runFileUploadProgress(event);
            } else if (event.type === HttpEventType.Response) {
              this.displayUploadedMessage('File(s) Uploaded!', 'text-success');
              this.keyPaperUploadedSource.next();
            }
          },
          error1 => this.errorProcessingUpload(error1)
        );
    });
  }

  public refreshIbb(): Promise<void> {
    return new Promise(resolve => {
      this.observeDownload(
        this.http.request(new HttpRequest('POST',
        `/api/rms/indexcompanies/refreshIbb`, undefined, { responseType: 'blob' }))
          .pipe(finalize(() => {
 resolve();
})), 'IBB_DOWNLOAD_Feedback.xlsx'
      );
    });
  }

  public refreshXbi(): Promise<void> {
    return new Promise(resolve => {
      this.observeDownload(
        this.http.request(new HttpRequest('POST',
        `/api/rms/indexcompanies/refreshXbi`, undefined, { responseType: 'blob' }))
          .pipe(finalize(() => resolve())) , 'XBI_Download_Feedback.xlsx'
      );
    });
  }

  public observeDownload(obs: Observable<HttpEvent<unknown>>, filename: string): void {
    obs.subscribe(
      event => {
        if (event.type === HttpEventType.UploadProgress) {
          this.runFileUploadProgress(event);
        } else if (event.type === HttpEventType.Response) {
          const response = event.body;
          const dataType = 'application/octet-stream';
          const binaryData = [];
          binaryData.push(response);
          const downloadLink = document.createElement('a');
          downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
          if (filename) {
            downloadLink.setAttribute('download', filename);
          }
          document.body.appendChild(downloadLink);
          downloadLink.click();
          this.bannerNotificationsService.success('File Uploaded!');
          this.displayUploadedMessage('File Uploaded!', 'text-success');
        }
      },
      error1 => this.errorProcessingUpload(error1)
    );
  }

  public indexHoldingsUpload(files: FileList, endpoint: string, inputId: string, filename: string): Promise<void> {
    return new Promise<void>(resolve => {
      const formData: FormData = this.processFiles(files);
      this.observeDownload(
      this.http.request(new HttpRequest('POST', `/api/rms/${endpoint}`, formData, { reportProgress: true, responseType: 'blob' })).pipe(
        finalize(() => {
          this.resetOnFileProcess(inputId);
          resolve();
        })), filename);
    });
  }

  private processFiles(files: FileList): FormData {
    this.fileUploadProgress = 0;
    this.uploadInProgress = true;
    this.uploadMessage = `<span class="text-info"><em>Uploading...</em></span>`;
    const formData: FormData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('file', files.item(i), files.item(i).name);
    }
    return formData;
  }

  private runFileUploadProgress(event: HttpProgressEvent): void {
    this.fileUploadProgress = Math.round(100 * event.loaded / event.total);
  }

  private resetOnFileProcess(inputId: string): void {
    if (inputId) {
      (document.getElementById(inputId) as HTMLInputElement).value = null;
    }
    this.fileUploadProgress = 0;
    this.uploadInProgress = false;
  }

  private errorProcessingUpload(notificationMessage: string): void {
    this.bannerNotificationsService.error(notificationMessage);
    this.displayUploadedMessage('Error Uploading', 'text-danger');
  }
}
